import React from 'react';
import { Grid, Box, FormHelperText } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import { EsgSingleSelectControlledField, TextField, EsgDatePickerField } from 'components';
import { getDateFormatset } from 'utils';
import { useNotify } from 'hooks';
import { fieldStyles } from '../../styles';

interface FormFieldsProps {
    control: any;
    setValue: any;
    allowedValues: any;
    clickType?: string;
    notesError: boolean;
    setNotesError: (error: boolean) => void;
    isNotesOnlyMode: boolean;
    isSpendBasedEstimation: boolean;
    editCollector?: any;
    availableUnitDescriptions: any[];
}

export const FormFields: React.FC<FormFieldsProps> = ({
    control,
    setValue,
    allowedValues,
    clickType,
    notesError,
    setNotesError,
    isNotesOnlyMode,
    isSpendBasedEstimation,
    editCollector,
    availableUnitDescriptions
}) => {
    const notify = useNotify();

    const watchedUtilityType = useWatch({ control, name: "utilityTypeName" });
    const watchedUtilitySubType = useWatch({ control, name: "utilitySubType" });
    const watchedAllowedDocuments = useWatch({ control, name: "allowedDocuments" });
    const watchedStartDate = useWatch({ control, name: "startDate" });
    const watchedFrequency = useWatch({ control, name: "frequency" });
    const watchedConsumptionUnit = useWatch({ control, name: "consumptionUnit" });
    const watchedCurrency = useWatch({ control, name: "currency" });
    const watchedScope = useWatch({ control, name: "scope" });
    // Memoized options for dropdowns
    const dropdownOptions = {
        utilityTypes: allowedValues?.utilityTypes?.map((ut: any) => ({
            label: ut.name,
            value: ut.name,
        })) || [],

        utilitySubtypes: watchedUtilityType && allowedValues?.utilitySubtypesMap?.[watchedUtilityType]?.subtypes
            ? allowedValues.utilitySubtypesMap[watchedUtilityType].subtypes.map((subtype: string) => ({
                label: subtype,
                value: subtype,
            }))
            : [],

        uploadTypes: allowedValues?.uploadTypes?.map((type: string) => ({
            label: type,
            value: type,
        })) || [],

        frequency: allowedValues?.frequency?.map((freq: string) => ({
            label: freq,
            value: freq,
        })) || [],

        currencies: allowedValues?.currencies?.map((currency: string) => ({
            label: currency,
            value: currency,
        })) || [],

        scopes: [
            { label: "No Scope", value: "NO_SCOPE" },
            { label: "Scope 1", value: "scope1" },
            { label: "Scope 2", value: "scope2" },
        ],
    };

    const handleUtilityTypeChange = (value: string | number) => {
        const selectedUtilityType = allowedValues?.utilityTypes?.find((ut: any) => ut.name === value);
        const ghgEmission = selectedUtilityType?.ghgEmission;
        const scopeValue = ghgEmission === "No Scope" ? "NO_SCOPE" : ghgEmission;

        setValue("utilityTypeName", value as string);
        setValue("scope", scopeValue);
        setValue("utilitySubType", ""); // Reset subtype when utility type changes
    };

    if (isNotesOnlyMode) {
        return (
            <Box sx={fieldStyles.notesContainer}>
                <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            size="small"
                            label="Notes"
                            multiline
                            rows={3}
                            error={notesError}
                            sx={fieldStyles.multilineTextField}
                            helperText={
                                notesError ? (
                                    <FormHelperText sx={fieldStyles.errorHelperText}>
                                        Notes length should not exceed 250 characters
                                    </FormHelperText>
                                ) : ""
                            }
                            onChange={(e) => {
                                const { value } = e.target;
                                if (value.length <= 250) {
                                    setNotesError(false);
                                    field.onChange(value);
                                } else {
                                    setNotesError(true);
                                }
                            }}
                        />
                    )}
                />
            </Box>
        );
    }

    // Check if utility subtype is required (not optional) for the current utility type
    const isSubtypeRequired = watchedUtilityType &&
        dropdownOptions.utilitySubtypes.length > 0 &&
        !allowedValues?.utilitySubtypesMap?.[watchedUtilityType]?.optional;

    return (
        <>
            {/* Utility Type */}
            <Grid item xs={6}>
                <EsgSingleSelectControlledField
                    label="Utility Type"
                    selectedValue={watchedUtilityType}
                    setSelectedValue={handleUtilityTypeChange}
                    options={dropdownOptions.utilityTypes}
                    placeholder="Select Utility Type"
                    width="100%"
                    required
                />
            </Grid>

            {/* Utility Subtype */}
            <Grid item xs={6}>
                <EsgSingleSelectControlledField
                    label="Utility Subtype"
                    selectedValue={watchedUtilitySubType}
                    setSelectedValue={(value) => setValue("utilitySubType", value as string)}
                    options={dropdownOptions.utilitySubtypes}
                    disabled={!watchedUtilityType || !dropdownOptions.utilitySubtypes.length}
                    placeholder={!watchedUtilityType || !dropdownOptions.utilitySubtypes.length ? "-" : "Select Utility Subtype"}
                    width="100%"
                    allowDeselect
                    required={isSubtypeRequired}
                />
            </Grid>

            {/* Account Number */}
            <Grid item xs={6}>
                <Controller
                    name="accountNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            {...field}
                            size="small"
                            sx={fieldStyles.textField}
                            label="Account Number"
                            error={!!error}
                            helperText={error?.message}
                            required
                        />
                    )}
                />
            </Grid>

            {/* Meter Number */}
            <Grid item xs={6}>
                <Controller
                    name="meterNumber"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            {...field}
                            size="small"
                            sx={fieldStyles.textField}
                            label="Meter Number"
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />
            </Grid>

            {/* Utility Provider Name */}
            <Grid item xs={6}>
                <Controller
                    name="providerName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            sx={fieldStyles.textField}
                            size="small"
                            {...field}
                            label="Utility Provider Name"
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />
            </Grid>

            {/* Document Types */}
            <Grid item xs={6}>
                <EsgSingleSelectControlledField
                    label="Document Type"
                    selectedValue={watchedAllowedDocuments}
                    setSelectedValue={(value) => setValue("allowedDocuments", value as string)}
                    options={dropdownOptions.uploadTypes}
                    placeholder="Select Document Type"
                    width="100%"
                    required
                />
            </Grid>

            {/* Start Date */}
            <Grid item xs={6}>
                <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                        <EsgDatePickerField
                            label="Start Date"
                            openTo="year"
                            value={field.value ? dayjs.utc(field.value) : null}
                            onChange={(date) => field.onChange(date?.format("YYYY-MM-DDT00:00:00[Z]"))}
                            format={getDateFormatset()}
                            clearDate={false}
                            width="100%"
                            placeholdervalue={getDateFormatset()}
                            required
                        />
                    )}
                />
            </Grid>

            {/* End Date */}
            <Grid item xs={6}>
                <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) =>
                        <EsgDatePickerField
                            label="End Date"
                            openTo="year"
                            value={field.value ? dayjs.utc(field.value) : null}
                            onChange={(date) => {
                                // Validate that the date is valid
                                if (!date || !date.isValid()) {
                                    notify.error("Please enter a valid date");
                                    return;
                                }

                                const givenDate = date;
                                const startDate = watchedStartDate;

                                // Validate end date is after start date
                                if (startDate && givenDate.isBefore(dayjs.utc(startDate))) {
                                    notify.error("End date must be after start date");
                                    return;
                                }

                                // Validate based on clickType (Enable/Disable)
                                if (givenDate.isBefore(dayjs()) && clickType === "Enable") {
                                    notify.error("An active collector cannot have an end date in the past");
                                    return;
                                }

                                if (givenDate.isAfter(dayjs()) && clickType === "Disable") {
                                    notify.error("A deactivated collector cannot have an end date in the future");
                                    return;
                                }

                                // All validations passed, update the field
                                field.onChange(date.format("YYYY-MM-DDT00:00:00[Z]"));
                            }}
                            format={getDateFormatset()}
                            clearDate={false}
                            width="100%"
                            placeholdervalue={getDateFormatset()}
                            minDate={clickType === "Enable" ? dayjs() : undefined}
                            maxDate={clickType === "Disable" ? dayjs() : undefined}
                            required
                        />
                    }
                />
            </Grid>

            {/* Frequency */}
            <Grid item xs={6}>
                <EsgSingleSelectControlledField
                    label="Frequency"
                    selectedValue={watchedFrequency}
                    setSelectedValue={(value) => setValue("frequency", value as string)}
                    options={dropdownOptions.frequency}
                    placeholder="Select Frequency"
                    width="100%"
                    required
                />
            </Grid>

            {/* Consumption Unit */}
            <Grid item xs={6}>
                <EsgSingleSelectControlledField
                    label="Consumption Unit"
                    selectedValue={watchedConsumptionUnit}
                    setSelectedValue={(value) => setValue("consumptionUnit", value as string)}
                    options={availableUnitDescriptions.map(unit => ({
                        label: `${unit.unit} (${unit.description})`,
                        value: unit.unit,
                    }))}
                    disabled={!watchedUtilityType}
                    placeholder={!watchedUtilityType ? "-" : "Select Consumption Unit"}
                    width="100%"
                    required
                />
            </Grid>

            {/* Currency */}
            <Grid item xs={6}>
                <EsgSingleSelectControlledField
                    label="Currency"
                    selectedValue={watchedCurrency}
                    setSelectedValue={(value) => setValue("currency", value as string)}
                    options={dropdownOptions.currencies}
                    placeholder="Select Currency"
                    width="100%"
                    required
                />
            </Grid>

            {/* Scope */}
            <Grid item xs={6}>
                <EsgSingleSelectControlledField
                    label="GHG Emission"
                    selectedValue={watchedScope}
                    setSelectedValue={(value) => setValue("scope", value as string)}
                    options={dropdownOptions.scopes}
                    placeholder="Select Scope"
                    width="100%"
                    required
                />
            </Grid>

            {/* Unit Cost (only show if spend based estimation is enabled) */}
            {isSpendBasedEstimation && (
                <Grid item xs={6}>
                    <Controller
                        name="unitCost"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                size="small"
                                label="Unit Cost"
                                disabled
                                sx={fieldStyles.unitCostField}
                                value={
                                    editCollector && editCollector.unitCost !== null
                                        ? editCollector.unitCost
                                        : (!watchedCurrency || !watchedConsumptionUnit ? "-" : `${watchedCurrency}/${watchedConsumptionUnit}`)
                                }
                                helperText={
                                    <Box sx={fieldStyles.unitCostHelperText}>
                                        Define Unit Cost within the Utility Providers table to see historical Unit Cost for this country
                                    </Box>
                                }
                                fullWidth
                                variant="outlined"
                            />
                        )}
                    />
                </Grid>
            )}
        </>
    );
};
